import { useEffect, useRef, useState } from 'react';
import { Button, IconButton, TextField } from '../../ui/controls';
import { Icon } from '../chrome/icons';
import { Menu, useMenu } from '../../ui/menu';
import { keyBelongsToField } from '../../ui/keys';
import type { FileVersion } from '../../services/versions';
import { NAVIGATION_WIDTH } from '../shell/NavigationPanel';
import { versionGroups, versionLines } from './versionLabels';
import { tileUrl } from '../content/tileUrl';

/**
 * Show all versions (W5-AR1): the whole history where Layers sits, named ones first and then
 * by day. A version pressed is previewed over the canvas with Restore and Copy as new file;
 * nothing changes until Restore. Opened from Document ▸ Version history ▸ (pick B's submenu).
 */
export function VersionsPanel({
  versions,
  more,
  failed,
  chosen,
  onChoose,
  onRename,
  onMore,
  onRetry,
  onClose,
}: {
  versions: readonly FileVersion[] | null;
  more: boolean;
  failed: boolean;
  chosen: string | null;
  onChoose: (version: FileVersion | null) => void;
  onRename: (version: FileVersion) => void;
  onMore: () => void;
  onRetry: () => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    panel.current?.focus({ preventScroll: true });
    // Escape closes it, unless something else took that Escape first (as the Layers panel does).
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || keyBelongsToField(event.target, event.key)) return;
      window.setTimeout(() => {
        if (!event.defaultPrevented) closeRef.current();
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const groups = versions ? versionGroups(versions) : [];
  return (
    <aside ref={panel} tabIndex={-1} aria-label="Version history" className="workspace-navigation workspace-navigation-content glass ui-pop pointer-events-auto absolute z-20 flex flex-col overflow-hidden outline-none" style={{ width: NAVIGATION_WIDTH }}>
      <header className="flex h-[44px] shrink-0 items-center gap-[8px] pl-[14px] pr-[8px] text-label">
        <Icon name="clock" size={16} />
        <h2 className="min-w-0 flex-1 truncate text-[13px] font-semibold">Version history</h2>
        <IconButton icon="close" label="Close version history" onClick={onClose} />
      </header>
      <p className="shrink-0 px-[14px] pb-[8px] text-[12px] text-label-2">Saved every few minutes while you work.</p>
      <div className="ui-scroll min-h-0 flex-1 overflow-y-auto px-[6px] pb-[10px]">
        {versions === null && !failed
          ? [0, 1, 2, 3].map((index) => (
              <div key={index} className="flex h-[56px] items-center gap-[10px] px-[8px]" aria-hidden>
                <span className="h-[40px] w-[56px] shrink-0 rounded-[6px] bg-fill-1" />
                <span className="flex flex-1 flex-col gap-[6px]">
                  <span className="h-[10px] w-[60%] rounded-full bg-fill-1" />
                  <span className="h-[8px] w-[40%] rounded-full bg-fill-1" />
                </span>
              </div>
            ))
          : null}
        {failed && versions === null ? (
          <div className="flex items-center gap-[8px] px-[8px] py-[10px] text-[12px] text-label-2">
            <span className="flex-1">The history could not be read.</span>
            <Button size="small" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : null}
        {versions !== null && versions.length === 0 ? <p className="px-[8px] py-[10px] text-[12px] text-label-2">No versions yet. One is saved after a few minutes of work, or name this one from the title menu.</p> : null}
        {groups.map((group) => (
          <section key={group.label} aria-label={group.label}>
            <h3 className="px-[8px] pb-[4px] pt-[10px] text-[11px] font-semibold leading-[14px] text-label-2">{group.label}</h3>
            {group.versions.map((version) => (
              <VersionRow key={version.id} version={version} chosen={chosen === version.id} onChoose={() => onChoose(chosen === version.id ? null : version)} onRename={() => onRename(version)} />
            ))}
          </section>
        ))}
        {more ? (
          <div className="px-[8px] pt-[8px]">
            <Button size="small" variant="borderless" onClick={onMore}>
              Show older versions
            </Button>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

function VersionRow({ version, chosen, onChoose, onRename }: { version: FileVersion; chosen: boolean; onChoose: () => void; onRename: () => void }) {
  const { title, detail } = versionLines(version);
  const menu = useMenu();
  return (
    <div className={`group flex h-[56px] items-center gap-[10px] rounded-[10px] px-[8px] ${chosen ? 'bg-fill-selected' : 'hover:bg-fill-1'}`}>
      <button type="button" aria-pressed={chosen} onClick={onChoose} className="flex min-w-0 flex-1 items-center gap-[10px] text-left outline-none">
        <span className="grid h-[40px] w-[56px] shrink-0 place-items-center overflow-hidden rounded-[6px] bg-fill-1 shadow-[inset_0_0_0_1px_var(--separator)]">
          {version.thumbnailUrl ? <img src={tileUrl(version.thumbnailUrl)} alt="" className="size-full object-cover" draggable={false} /> : null}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13px] leading-[17px] text-label">{title}</span>
          <span className="truncate text-[11px] leading-[14px] text-label-2">{detail}</span>
        </span>
      </button>
      <IconButton ref={menu.ref} icon="more" label={`More for ${title}`} onClick={menu.toggle} className={`pointer-coarse:size-[44px]! ${chosen || menu.open ? '' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100'}`} />
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} items={[{ id: 'rename', label: version.name ? 'Rename…' : 'Name this version…', onSelect: onRename }]} label={`More for ${title}`} />
    </div>
  );
}

/**
 * The chosen version over the canvas: its first artboard as drawn, and a bar with what it is,
 * Copy as new file and Restore. Nothing changes until Restore; Escape or Done goes back.
 */
export function VersionPreview({
  version,
  top,
  left,
  right,
  busy,
  onRestore,
  onCopy,
  onDone,
}: {
  version: FileVersion;
  top: number;
  left: number;
  right: number;
  busy: boolean;
  onRestore: () => void;
  onCopy: () => void;
  onDone: () => void;
}) {
  const { title } = versionLines(version);
  return (
    <div className="pointer-events-none absolute bottom-0 z-[15] max-md:z-[35]" style={{ top, left, right }}>
      <div className="pointer-events-auto absolute inset-0 grid place-items-center bg-canvas p-[64px_24px_24px]" onPointerDown={(event) => event.stopPropagation()}>
        {version.thumbnailUrl ? (
          <img src={version.thumbnailUrl} alt={`The file as it was at ${title}`} className="max-h-full max-w-full rounded-[4px] object-contain shadow-[0_8px_32px_rgba(0,0,0,0.25)]" draggable={false} />
        ) : (
          <p className="text-[13px] text-label-2">{version.thumbnailPending ? 'Drawing a preview…' : 'No preview for this version.'}</p>
        )}
      </div>
      <div role="toolbar" aria-label="Version" className="glass-thick ui-pop pointer-events-auto absolute left-1/2 top-[12px] flex -translate-x-1/2 items-center gap-[8px] rounded-full py-[5px] pl-[14px] pr-[5px] text-[13px] text-label max-md:w-[calc(100%-24px)] max-md:flex-wrap max-md:justify-center max-md:rounded-[18px] max-md:px-[10px] max-md:py-[8px]">
        <span className="max-w-[220px] truncate max-md:w-full max-md:max-w-none max-md:text-center">{title}</span>
        <Button size="small" variant="borderless" onClick={onDone}>
          Done
        </Button>
        <Button size="small" onClick={onCopy} disabled={busy}>
          Copy as new file
        </Button>
        <Button size="small" variant="prominent" onClick={onRestore} disabled={busy}>
          Restore
        </Button>
      </div>
    </div>
  );
}

/** Name current version… and Rename…: a name, Cancel and Save. Return saves, Escape cancels. */
export function NameVersionDialog({ title, initial, onSave, onCancel }: { title: string; initial: string; onSave: (name: string) => void; onCancel: () => void }) {
  const [name, setName] = useState(initial);
  const trimmed = name.trim();
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    // The menu that opened this gives focus back to its button as it closes, a frame later:
    // the field takes it after that, or the first letters typed would go to the canvas.
    const focus = () => form.current?.querySelector('input')?.focus();
    const frame = requestAnimationFrame(() => requestAnimationFrame(focus));
    // It owns the keyboard while it is up: a key aimed anywhere else goes to the field, never to
    // the canvas behind (where T, E, F and the rest are tools and views).
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        event.preventDefault();
        onCancel();
        return;
      }
      if (form.current?.contains(event.target as Node)) return;
      event.stopImmediatePropagation();
      if (event.key !== 'Tab') event.preventDefault();
      focus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onCancel]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/20 px-[16px]" onPointerDown={(event) => event.target === event.currentTarget && onCancel()}>
      <form
        ref={form}
        role="dialog"
        aria-modal="true"
        aria-labelledby="name-version-title"
        className="glass-thick ui-pop w-full max-w-[320px] rounded-[18px] p-[18px] text-label"
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed) onSave(trimmed.slice(0, 120));
        }}
      >
        <h2 id="name-version-title" className="pb-[10px] text-[15px] font-semibold">
          {title}
        </h2>
        <TextField label="Version name" value={name} onChange={setName} placeholder="Logo locked" autoFocus onEnter={() => trimmed && onSave(trimmed.slice(0, 120))} />
        <div className="mt-[16px] grid grid-cols-2 gap-[8px]">
          <Button full type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button full type="submit" variant="prominent" disabled={!trimmed}>
            Save
          </Button>
        </div>
      </form>
    </div>
  );
}
